import { useMemo } from 'react'
import { Badge, Box, Grid, GridItem, HStack, Text, Tooltip, VStack } from '@chakra-ui/react'
import type { FESchedule } from '@/types/parentTicket'
import { useThemeColors } from '@/hooks/useThemeColors'

interface FEScheduleCalendarProps {
  schedules: FESchedule[]
  month: Date
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function toDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function scheduleOverlapsDay(schedule: FESchedule, dayKey: string): boolean {
  const start = schedule.schedule_date?.slice(0, 10) ?? ''
  const end = (schedule.schedule_end_date ?? schedule.schedule_date)?.slice(0, 10) ?? start
  if (!start) return false
  return start <= dayKey && end >= dayKey
}

function formatTimeRange(schedule: FESchedule): string {
  const start = schedule.schedule_time?.slice(0, 5)
  const end = schedule.schedule_end_time?.slice(0, 5)
  if (start && end) return `${start}–${end}`
  if (start) return start
  return 'All day'
}

function roleColor(role: FESchedule['role']): string {
  return role === 'primary' ? 'blue' : 'purple'
}

export default function FEScheduleCalendar({ schedules, month }: FEScheduleCalendarProps) {
  const colors = useThemeColors()

  const calendarDays = useMemo(() => {
    const year = month.getFullYear()
    const monthIndex = month.getMonth()
    const firstOfMonth = new Date(year, monthIndex, 1)
    const lastOfMonth = new Date(year, monthIndex + 1, 0)
    const startOffset = firstOfMonth.getDay()
    const daysInMonth = lastOfMonth.getDate()

    const cells: Array<{ date: Date | null; key: string }> = []
    for (let i = 0; i < startOffset; i += 1) {
      cells.push({ date: null, key: `pad-start-${i}` })
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, monthIndex, day)
      cells.push({ date, key: toDateKey(date) })
    }
    while (cells.length % 7 !== 0) {
      cells.push({ date: null, key: `pad-end-${cells.length}` })
    }

    return cells
  }, [month])

  const schedulesByDay = useMemo(() => {
    const map = new Map<string, FESchedule[]>()
    for (const cell of calendarDays) {
      if (!cell.date) continue
      const dayKey = toDateKey(cell.date)
      const daySchedules = schedules.filter((s) => scheduleOverlapsDay(s, dayKey))
      map.set(dayKey, daySchedules)
    }
    return map
  }, [calendarDays, schedules])

  const monthLabel = month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

  return (
    <VStack align="stretch" spacing={3}>
      <Text fontWeight="semibold" color={colors.heading}>
        {monthLabel}
      </Text>
      <Grid templateColumns="repeat(7, minmax(0, 1fr))" gap={2}>
        {WEEKDAY_LABELS.map((label) => (
          <GridItem key={label}>
            <Text fontSize="xs" fontWeight="semibold" color={colors.textMuted} textAlign="center">
              {label}
            </Text>
          </GridItem>
        ))}
        {calendarDays.map((cell) => {
          if (!cell.date) {
            return <GridItem key={cell.key} minH="110px" />
          }

          const dayKey = toDateKey(cell.date)
          const daySchedules = schedulesByDay.get(dayKey) ?? []
          const isToday = dayKey === toDateKey(new Date())

          return (
            <GridItem
              key={cell.key}
              minH="110px"
              borderWidth="1px"
              borderColor={isToday ? 'blue.400' : colors.border}
              borderRadius="md"
              bg={colors.cardBg}
              p={2}
            >
              <Text fontSize="sm" fontWeight={isToday ? 'bold' : 'medium'} color={colors.heading} mb={1}>
                {cell.date.getDate()}
              </Text>
              <VStack align="stretch" spacing={1} maxH="80px" overflowY="auto">
                {daySchedules.slice(0, 4).map((schedule) => {
                  const feName = schedule.field_engineer?.name ?? `FE #${schedule.fe_user_id}`
                  const label = `${formatTimeRange(schedule)} · ${feName}`
                  return (
                    <Tooltip
                      key={`${schedule.id}-${dayKey}`}
                      label={`${schedule.ticket_subject ?? `Ticket #${schedule.ticket_id}`} (${schedule.role})`}
                      hasArrow
                    >
                      <Box
                        px={1.5}
                        py={0.5}
                        borderRadius="sm"
                        bg={`${roleColor(schedule.role)}.100`}
                        borderLeftWidth="3px"
                        borderLeftColor={`${roleColor(schedule.role)}.500`}
                      >
                        <HStack spacing={1} justify="space-between">
                          <Text fontSize="10px" noOfLines={1} color={colors.heading}>
                            {label}
                          </Text>
                          {schedule.external_dispatch_status ? (
                            <Badge colorScheme="gray" fontSize="9px">
                              {schedule.external_dispatch_status}
                            </Badge>
                          ) : null}
                        </HStack>
                      </Box>
                    </Tooltip>
                  )
                })}
                {daySchedules.length > 4 ? (
                  <Text fontSize="10px" color={colors.textMuted}>
                    +{daySchedules.length - 4} more
                  </Text>
                ) : null}
              </VStack>
            </GridItem>
          )
        })}
      </Grid>
    </VStack>
  )
}

export { toDateKey }
